import React, { useEffect, useState } from 'react';
import { isValid } from 'date-fns';
import { baseComponents, hooks as commonHooks } from '@sitebuilder/common';
import { IntegratedDatePickerOptions } from './GrowthChart';
import 'react-datepicker/dist/react-datepicker.css';

interface CustomDatePickerInputProps extends IntegratedDatePickerOptions {
    value?: string;
    onClick?: () => void;
}

export const CustomDatePickerInput: React.FC<CustomDatePickerInputProps> = (props) => {
    const { value, onClick, ...rest } = props;

    // Function to render the icon based on the provided options
    const renderIcon = () => {
        return (
            <baseComponents.iconRender.IconRender
                showSpan={true}
                className={`custom-date-picker-icon-${
                    rest?.datePickerIconPosition === 'Left' ? 'left' : 'right'
                }`}
                iconType={rest?.datePickerIconType}
                iconName={rest.datePickerIcon}
                iconClassName={''}
                imageUrl={rest?.datePickerImage}
                imageClassName={'custom-date-picker-image'}
                imageAlt={'dropdown-toggle-open'}
            />
        );
    };

    return (
        <button className="custom-date-picker-input" onClick={onClick}>
            {rest?.datePickerIconPosition === 'Left' ? renderIcon() : null}
            <span className="custom-date-picker-value">{value}</span>
            {rest?.datePickerIconPosition === 'Right' ? renderIcon() : null}
        </button>
    );
};

interface IntegratedDatePickerProps {
    data: any;
    minDate: any;
    maxDate: any;
    onChangeDateRange: (minDate: any, maxDate: any) => void;
}

const IntegratedDatePicker: React.FC<IntegratedDatePickerOptions & IntegratedDatePickerProps> = (
    props
) => {
    const { data, startDateLabel, endDateLabel, dateFormat, ...rest } = props;
    const [startDate, setStartDate] = useState<any>(new Date(rest?.minDate));
    const [endDate, setEndDate] = useState<any>(new Date(rest?.maxDate));
    const i18n = commonHooks.useLocalization();

    // Call the onChangeDateRange callback when the start or end date changes
    useEffect(() => {
        if (startDate && isValid(startDate) && endDate && isValid(endDate)) {
            const _startDate = {
                day: startDate.getDate(),
                month: startDate.getMonth(),
                year: startDate.getFullYear(),
            };

            const _endDate = {
                day: endDate.getDate(),
                month: endDate.getMonth(),
                year: endDate.getFullYear(),
            };
            rest.onChangeDateRange(_startDate, _endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    return (
        <div className="integrated-date-picker-container">
            <div className="start-date-container">
                <label
                    htmlFor="startDateInput"
                    aria-labelledby={i18n.format(startDateLabel, data)}
                    tabIndex={0}
                >
                    {i18n.format(startDateLabel, data)}
                </label>
                <baseComponents.datePicker.DatePicker
                    id="startDateInput"
                    selected={startDate}
                    onChange={(date: any) => setStartDate(date)}
                    dateFormat={dateFormat}
                    customInput={<CustomDatePickerInput {...props} />}
                    closeOnScroll
                />
            </div>
            <div className="end-date-container">
                <label
                    htmlFor="endDateInput"
                    aria-labelledby={i18n.format(endDateLabel, data)}
                    tabIndex={0}
                >
                    {i18n.format(endDateLabel, data)}
                </label>
                <baseComponents.datePicker.DatePicker
                    id="endDateInput"
                    selected={endDate}
                    minDate={startDate}
                    onChange={(date: any) => setEndDate(date)}
                    dateFormat={dateFormat}
                    customInput={<CustomDatePickerInput {...props} />}
                    closeOnScroll
                />
            </div>
        </div>
    );
};

export default IntegratedDatePicker;
